import type { AST } from '../ast';
import { Ty } from '../ty';
import { UnionAll, Relation, Values } from '../builder';
import { evalRelIR } from './eval-rel-ir';
import _ from 'lodash';

export const EMPTY_DB: DB = { '@@write-schema': {} };

type RelStorageNode =
  | { readonly data: AST._ValuesContract; readonly t: 'table' }
  | { readonly data: AST.RelIR; readonly t: 'view' };

export type DB = {
  readonly [schema: string]: {
    readonly [name: string]: RelStorageNode;
  };
};

const insertInto = (
  table: AST.TableDescription,
  contents: Ty.Row[] | Relation,
  db: DB
): DB =>
  updateStorageNode(db, table, (existing) => {
    if (existing === null) {
      throw new Error(`"${table.schema}".${table.name} does not exist`);
    }
    if (existing.t === 'view') {
      throw new Error(
        `"${table.schema}".${table.name} is a view and cant be insterted into`
      );
    }
    const oldTable = Relation.fromAst(existing.data);

    const newTable = UnionAll([
      oldTable,
      contents instanceof Relation
        ? contents
        : Values(contents, oldTable.attributes),
    ]);

    const simplified: AST._ValuesContract = {
      t: 'values',
      values: [...evalRelIR(newTable.ir(), { db })],
      attributes: newTable.attributes,
    };

    return { t: 'table', data: simplified };
  });

const createOrReplaceView = (
  target: AST.TableIdentifier,
  relation: Relation,
  db: DB
): DB =>
  updateStorageNode(db, target, (existing) => {
    if (existing !== null && existing.t === 'table') {
      throw new Error('Cant replace a table with a view');
    }

    return { t: 'view', data: relation.ir() };
  });

const createTable = (target: AST.TableDescription, db: DB): DB =>
  updateStorageNode(db, target, (existing) => {
    if (existing !== null) {
      throw new Error(
        `Table "${target.schema}"."${target.name}" already exists`
      );
    }

    const data: AST._ValuesContract = {
      t: 'values',
      attributes: _.mapValues(target.attributes, (ty) => Ty.shorthandToTy(ty)),
      values: [],
    };
    return { t: 'table', data };
  });

const dropTable = (target: AST.TableIdentifier, db: DB): DB =>
  updateStorageNode(db, target, () => null);

const updateStorageNode = (
  db: DB,
  ref: AST.TableIdentifier,
  callback: (node: RelStorageNode | null) => RelStorageNode | null
): DB => {
  const { name, schema } = ref;

  const existingSchema = db[schema];

  if (!existingSchema) {
    throw new Error(`Schema "${schema}" Not Found`);
  }

  const { [name]: existingRelDiskNode, ...restOfSchema } = existingSchema;
  const newNode = Object.freeze(callback(existingRelDiskNode ?? null));

  const newSchema = Object.freeze(
    newNode === null ? restOfSchema : { ...restOfSchema, [name]: newNode }
  );

  return Object.freeze({ ...db, [schema]: Object.freeze(newSchema) });
};

const truncate = (target: AST.TableIdentifier, db: DB): DB =>
  updateStorageNode(db, target, (existing) => {
    if (existing === null) {
      throw new Error(`Table "${target.schema}"."${target.name}" not found`);
    }

    if (existing.t === 'view') {
      throw new Error('Can not truncate a view');
    }

    const data: AST._ValuesContract = {
      t: 'values',
      attributes: existing.data.attributes,
      values: [],
    };

    return { t: 'table', data };
  });

export const DLL = {
  dropTable,
  insertInto,
  createTable,
  createOrReplaceView,
  truncate,
};
